/**
 * Activity Service
 * 
 * Centralized service for fetching activities from Supabase database
 * Replaces hardcoded activities with dynamic database queries
 */

import { supabase } from '@/integrations/supabase/client';
import { Activity } from '@/types';
import { parsePriceTiers } from '@/services/pricingService';
import { normalizeListItems } from '@/utils/listItemUtils';

/**
 * Transform database activity to frontend Activity type
 */
function transformActivity(dbActivity: any): Activity {
  return {
    id: dbActivity.id,
    slug: dbActivity.slug ?? dbActivity.id,
    name: dbActivity.name,
    category: dbActivity.category?.slug || dbActivity.category_id,
    shortDescription: dbActivity.short_description ?? '',
    address: dbActivity.address,
    city: dbActivity.city,
    postalCode: dbActivity.postal_code,
    latitude: dbActivity.latitude,
    longitude: dbActivity.longitude,
    rating: dbActivity.rating,
    reviewCount: dbActivity.review_count,
    priceRange: dbActivity.price_range,
    mainImage: dbActivity.main_image,
    images: dbActivity.images || [],
    priceTiers: parsePriceTiers(dbActivity.price_tiers),
    duration: dbActivity.duration,
    capacity: dbActivity.capacity,
    minParticipants: dbActivity.min_participants ?? undefined,
    defaultMaxBookingsPerSlot: dbActivity.default_max_bookings_per_slot ?? undefined,
    popular: dbActivity.popular,
    features: dbActivity.features || [],
    minAge: dbActivity.min_age,
    maxAge: dbActivity.max_age,
    whatToWear: dbActivity.what_to_wear || [],
    included: dbActivity.included || [],
    notIncluded: dbActivity.not_included || [],
    importantRules: dbActivity.important_rules || [],
    cancellationConditions: dbActivity.cancellation_conditions || [],
    accessibility: {
      pmrAccess: dbActivity.pmr_access,
      parkingAvailable: dbActivity.parking_available,
    },
    // Additional fields from joins
    professionalId: dbActivity.professional_id,
    phoneNumber: dbActivity.professional?.phone,
    isActive: dbActivity.is_active ?? true,
    custom_category_name: dbActivity.custom_category_name ?? null,
  };
}

/**
 * Fetch all active activities
 * @returns Array of activities
 */
export async function getAllActivities(): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('is_active', true)
      .order('popular', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error('Failed to fetch activities:', error);
    return [];
  }
}

/**
 * Fetch all active activities with venue short_description for search.
 * Enriches activities with venueShortDescription so search can match venue descriptions.
 * @returns Array of activities with venueShortDescription populated
 */
export async function getAllActivitiesForSearch(): Promise<Activity[]> {
  try {
    const activities = await getAllActivities();
    if (activities.length === 0) return [];

    const professionalIds = [...new Set(activities.map((a) => a.professionalId).filter(Boolean))] as string[];
    if (professionalIds.length === 0) return activities;

    const { data: professionalsData, error: profError } = await supabase
      .from('professionals')
      .select('id, short_description, main_image')
      .in('id', professionalIds);

    if (profError) {
      console.warn('Failed to fetch professionals for search enrichment:', profError);
      return activities;
    }

    const venueDescByProId = new Map<string, string | null>();
    const venueMainImageByProId = new Map<string, string | null>();
    for (const p of professionalsData ?? []) {
      venueDescByProId.set(p.id, p.short_description ?? null);
      venueMainImageByProId.set(p.id, (p as { main_image?: string | null }).main_image ?? null);
    }

    return activities.map((a) => ({
      ...a,
      venueShortDescription: a.professionalId ? venueDescByProId.get(a.professionalId) ?? null : null,
      venueMainImage: a.professionalId ? venueMainImageByProId.get(a.professionalId) ?? null : null,
    }));
  } catch (error) {
    console.error('Failed to fetch activities for search:', error);
    return [];
  }
}

/**
 * Fetch a single activity by ID
 * @param id - Activity UUID
 * @returns Activity or null
 */
export async function getActivityById(id: string): Promise<Activity | null> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      console.error('Error fetching activity by ID:', error);
      throw error;
    }

    return data ? transformActivity(data) : null;
  } catch (error) {
    console.error(`Failed to fetch activity ${id}:`, error);
    return null;
  }
}

/**
 * Fetch a single active activity by slug.
 * @param slug - Activity slug, e.g. "escape-game-le-donjon-paris"
 */
export async function getActivityBySlug(slug: string): Promise<Activity | null> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching activity by slug:', error);
      throw error;
    }

    return data ? transformActivity(data) : null;
  } catch (error) {
    console.error(`Failed to fetch activity with slug "${slug}":`, error);
    return null;
  }
}

/**
 * Fetch venue-level cancellation conditions from professionals table.
 * Used for cancel modal and display on activity/venue pages.
 */
export async function getVenueCancellationConditions(professionalId: string): Promise<string[] | undefined> {
  try {
    const { data, error } = await supabase
      .from('professionals')
      .select('cancellation_conditions')
      .eq('id', professionalId)
      .single();

    if (error || !data) return undefined;
    return Array.isArray(data.cancellation_conditions) ? data.cancellation_conditions : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fetch activities by category, enriched with venue main_image for VenueCard display.
 * @param categorySlug - Category slug (e.g., 'bowling', 'escape-game')
 * @returns Array of activities with venueMainImage populated
 */
export async function getActivitiesByCategory(categorySlug: string): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories!inner(id, slug, name)
      `)
      .eq('is_active', true)
      .eq('category.slug', categorySlug)
      .order('popular', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error fetching activities by category:', error);
      throw error;
    }

    const activities = data ? data.map(transformActivity) : [];
    if (activities.length === 0) return activities;

    // Enrich with venue main_image from professionals (for VenueCard consistency)
    const professionalIds = [...new Set(activities.map((a) => a.professionalId).filter(Boolean))] as string[];
    if (professionalIds.length === 0) return activities;

    const { data: professionalsData, error: profError } = await supabase
      .from('professionals')
      .select('id, main_image')
      .in('id', professionalIds);

    if (profError) {
      console.warn('Failed to fetch professionals for category enrichment:', profError);
      return activities;
    }

    const venueMainImageByProId = new Map<string, string | null>();
    for (const p of professionalsData ?? []) {
      venueMainImageByProId.set(p.id, (p as { main_image?: string | null }).main_image ?? null);
    }

    return activities.map((a) => ({
      ...a,
      venueMainImage: a.professionalId ? venueMainImageByProId.get(a.professionalId) ?? null : null,
    }));
  } catch (error) {
    console.error(`Failed to fetch activities for category ${categorySlug}:`, error);
    return [];
  }
}

/**
 * Fetch activities by city
 * @param city - City name
 * @returns Array of activities
 */
export async function getActivitiesByCity(city: string): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('is_active', true)
      .ilike('city', city)
      .order('popular', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error fetching activities by city:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error(`Failed to fetch activities for city ${city}:`, error);
    return [];
  }
}

/**
 * Fetch popular activities
 * @param limit - Maximum number of activities to return
 * @returns Array of popular activities
 */
export async function getPopularActivities(limit: number = 6): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('is_active', true)
      .eq('popular', true)
      .order('rating', { ascending: false })
      .order('review_count', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching popular activities:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error('Failed to fetch popular activities:', error);
    return [];
  }
}

/**
 * Search activities with filters
 * @param query - Search query string
 * @param filters - Optional filters (category, city, priceRange, etc.)
 * @returns Array of matching activities
 */
export async function searchActivities(
  query?: string,
  filters?: {
    category?: string;
    city?: string;
    priceRange?: string;
    minRating?: number;
    latitude?: number;
    longitude?: number;
    radiusKm?: number;
  }
): Promise<Activity[]> {
  try {
    let queryBuilder = supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('is_active', true);

    // Text search (simple ILIKE for now, can be upgraded to full-text search)
    if (query && query.trim()) {
      queryBuilder = queryBuilder.or(
        `name.ilike.%${query}%,short_description.ilike.%${query}%,city.ilike.%${query}%`
      );
    }

    // Category filter
    if (filters?.category) {
      queryBuilder = queryBuilder.eq('category.slug', filters.category);
    }

    // City filter
    if (filters?.city) {
      queryBuilder = queryBuilder.ilike('city', `%${filters.city}%`);
    }

    // Price range filter
    if (filters?.priceRange) {
      queryBuilder = queryBuilder.eq('price_range', filters.priceRange);
    }

    // Rating filter
    if (filters?.minRating) {
      queryBuilder = queryBuilder.gte('rating', filters.minRating);
    }

    // Location-based search (if coordinates provided)
    // Note: This is a simplified version. For production, use PostGIS functions
    if (filters?.latitude && filters?.longitude && filters?.radiusKm) {
      // This will be implemented with PostGIS earth_box in production
      // For now, just return results without distance filtering
      console.warn('Location-based filtering not yet implemented');
    }

    // Order by relevance (popular first, then rating)
    queryBuilder = queryBuilder
      .order('popular', { ascending: false })
      .order('rating', { ascending: false })
      .order('name');

    const { data, error } = await queryBuilder;

    if (error) {
      console.error('Error searching activities:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error('Failed to search activities:', error);
    return [];
  }
}

/**
 * Fetch all activities for a specific venue (professional) - published only (for public pages)
 * @param professionalId - Professional/Venue UUID
 * @returns Array of published activities from the same venue
 */
export async function getActivitiesByVenue(professionalId: string): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('professional_id', professionalId)
      .eq('is_active', true)
      .order('popular', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error fetching activities by venue:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error(`Failed to fetch activities for venue ${professionalId}:`, error);
    return [];
  }
}

/**
 * Fetch all activities for a professional (drafts + published) - for pro dashboard
 * @param professionalId - Professional UUID
 * @returns Array of all activities owned by the professional
 */
export async function getActivitiesByPro(professionalId: string): Promise<Activity[]> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('professional_id', professionalId)
      .order('popular', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error fetching activities for pro:', error);
      throw error;
    }

    return data ? data.map(transformActivity) : [];
  } catch (error) {
    console.error(`Failed to fetch activities for pro ${professionalId}:`, error);
    return [];
  }
}

/**
 * Fetch a single activity by ID for pro owner (includes drafts)
 * @param id - Activity UUID
 * @param professionalId - Professional UUID (must own the activity)
 * @returns Activity or null if not found or not owned
 */
export async function getActivityByIdForPro(id: string, professionalId: string): Promise<Activity | null> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('id', id)
      .eq('professional_id', professionalId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching activity by ID for pro:', error);
      throw error;
    }

    return data ? transformActivity(data) : null;
  } catch (error) {
    console.error(`Failed to fetch activity ${id} for pro:`, error);
    return null;
  }
}

/**
 * Publish an activity (set is_active to true)
 * @param activityId - Activity UUID
 * @returns Updated activity
 */
export async function publishActivity(activityId: string): Promise<Activity> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .update({ is_active: true })
      .eq('id', activityId)
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .single();

    if (error) {
      console.error('Error publishing activity:', error);
      throw error;
    }

    return transformActivity(data);
  } catch (error) {
    console.error('Failed to publish activity:', error);
    throw error;
  }
}

/**
 * Unpublish an activity (set is_active to false, back to draft)
 * @param activityId - Activity UUID
 * @returns Updated activity
 */
export async function unpublishActivity(activityId: string): Promise<Activity> {
  try {
    const { data, error } = await supabase
      .from('activities')
      .update({ is_active: false })
      .eq('id', activityId)
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .single();

    if (error) {
      console.error('Error unpublishing activity:', error);
      throw error;
    }

    return transformActivity(data);
  } catch (error) {
    console.error('Failed to unpublish activity:', error);
    throw error;
  }
}

/**
 * Duplicate an activity - creates a copy as draft (schedule templates not copied)
 * @param activityId - Source activity UUID
 * @returns New activity (draft)
 */
export async function duplicateActivity(activityId: string): Promise<Activity> {
  try {
    const { data: source, error: fetchError } = await supabase
      .from('activities')
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .eq('id', activityId)
      .single();

    if (fetchError || !source) {
      throw new Error('Activité introuvable');
    }

    const { professional_id, name, category_id, short_description, address, city, postal_code, latitude, longitude, price_tiers, price_range, duration, capacity, min_participants, default_max_bookings_per_slot, main_image, images, features, min_age, max_age, what_to_wear, included, not_included, important_rules, cancellation_conditions, pmr_access, parking_available } = source;

    const { data: newActivity, error: insertError } = await supabase
      .from('activities')
      .insert({
        professional_id,
        category_id,
        name: `${name} (copie)`,
        description: null,
        short_description,
        address,
        city,
        postal_code,
        latitude,
        longitude,
        price_tiers,
        price_range,
        duration,
        capacity,
        min_participants,
        default_max_bookings_per_slot,
        main_image,
        images: images || [],
        features: features || [],
        min_age,
        max_age,
        what_to_wear: what_to_wear || [],
        included: included || [],
        not_included: not_included || [],
        important_rules: normalizeListItems(important_rules || []),
        cancellation_conditions: cancellation_conditions || [],
        pmr_access: pmr_access ?? false,
        parking_available: parking_available ?? false,
        is_active: false,
        popular: false,
        rating: 0,
        review_count: 0,
      })
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .single();

    if (insertError) {
      console.error('Error duplicating activity:', insertError);
      throw insertError;
    }

    return transformActivity(newActivity);
  } catch (error) {
    console.error('Failed to duplicate activity:', error);
    throw error;
  }
}

/**
 * Count active bookings for an activity (pending or confirmed, excludes cancelled)
 * @param activityId - Activity UUID
 * @returns Number of active bookings
 */
export async function getActivityBookingCount(activityId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('bookings')
      .select('*', { count: 'exact', head: true })
      .eq('activity_id', activityId)
      .in('status', ['pending', 'confirmed']);

    if (error) {
      console.error('Error counting activity bookings:', error);
      throw error;
    }

    return count ?? 0;
  } catch (error) {
    console.error('Failed to get activity booking count:', error);
    throw error;
  }
}

/**
 * Get planning summary for an activity (schedule templates + venue_availability slots)
 * @param activityId - Activity UUID
 * @returns { templatesCount, slotsCount }
 */
export async function getActivityPlanningSummary(activityId: string): Promise<{
  templatesCount: number;
  slotsCount: number;
}> {
  try {
    const [templatesResult, slotsResult] = await Promise.all([
      supabase
        .from('venue_schedule_templates')
        .select('*', { count: 'exact', head: true })
        .eq('activity_id', activityId),
      supabase
        .from('venue_availability')
        .select('*', { count: 'exact', head: true })
        .eq('activity_id', activityId),
    ]);

    if (templatesResult.error) {
      console.error('Error counting schedule templates:', templatesResult.error);
      throw templatesResult.error;
    }
    if (slotsResult.error) {
      console.error('Error counting venue availability:', slotsResult.error);
      throw slotsResult.error;
    }

    return {
      templatesCount: templatesResult.count ?? 0,
      slotsCount: slotsResult.count ?? 0,
    };
  } catch (error) {
    console.error('Failed to get activity planning summary:', error);
    throw error;
  }
}

/**
 * Delete an activity. Caller must verify: no active bookings, ownership, and user confirmation.
 * Cascades: venue_schedule_templates, venue_availability, venue_scraper_config, scraper_logs.
 * @param activityId - Activity UUID
 * @param professionalId - Professional UUID (must own the activity, verified via RLS)
 */
export async function deleteActivity(activityId: string, professionalId: string): Promise<void> {
  try {
    const existing = await getActivityByIdForPro(activityId, professionalId);
    if (!existing) {
      throw new Error('Activité introuvable ou accès refusé');
    }

    const { error } = await supabase
      .from('activities')
      .delete()
      .eq('id', activityId);

    if (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  } catch (error) {
    console.error('Failed to delete activity:', error);
    throw error;
  }
}

/**
 * Get category label from slug.
 * When customCategoryName is set (for "autre"), returns only the custom name (never "Autre: …").
 */
export function getCategoryLabel(category: string, customCategoryName?: string | null): string {
  if (customCategoryName && customCategoryName.trim()) {
    return customCategoryName.trim();
  }
  const labels: Record<string, string> = {
    'bowling': 'Bowling',
    'karting': 'Karting',
    'escape-game': 'Escape Game',
    'laser-game': 'Laser Game',
    'padel': 'Padel',
    'foot-indoor': 'Foot Indoor',
    'trampoline-park': 'Trampoline Park',
    'billard': 'Billard',
    'paintball-indoor': 'Paintball Indoor',
    'vr-arcade': 'VR Arcade',
    'autre': 'Autre',
  };
  return labels[category] || category;
}

/** Display label for an activity's category (uses custom name when set). */
export function getActivityCategoryLabel(activity: { category: string; custom_category_name?: string | null }): string {
  return getCategoryLabel(activity.category, activity.custom_category_name);
}

/**
 * Create a new activity
 * @param activityData - Activity data to insert
 * @returns Created activity
 */
export async function createActivity(activityData: {
  professionalId: string;
  categoryId: string;
  /** Custom display name when category is "autre". Required if category is autre. */
  customCategoryName?: string | null;
  name: string;
  shortDescription: string;
  address: string;
  city: string;
  postalCode: string;
  latitude?: number;
  longitude?: number;
  priceTiers: { maxParticipants: number; pricePerPerson: number }[];
  priceRange: string;
  duration: number;
  capacity: number;
  minParticipants?: number | null;
  defaultMaxBookingsPerSlot?: number | null;
  mainImage: string;
  images?: string[];
  features?: string[];
  minAge?: number;
  maxAge?: number;
  whatToWear?: string[];
  included?: string[];
  notIncluded?: string[];
  importantRules?: string[];
  cancellationConditions?: string[];
  pmrAccess?: boolean;
  parkingAvailable?: boolean;
  /** Default true for backward compatibility */
  isActive?: boolean;
}): Promise<Activity> {
  try {
    const isActive = activityData.isActive ?? true;
    const { data, error } = await supabase
      .from('activities')
      .insert({
        professional_id: activityData.professionalId,
        category_id: activityData.categoryId,
        custom_category_name: activityData.customCategoryName ?? null,
        name: activityData.name,
        description: null,
        short_description: activityData.shortDescription,
        address: activityData.address,
        city: activityData.city,
        postal_code: activityData.postalCode,
        latitude: activityData.latitude,
        longitude: activityData.longitude,
        price_tiers: activityData.priceTiers,
        price_range: activityData.priceRange,
        duration: activityData.duration,
        capacity: activityData.capacity,
        min_participants: activityData.minParticipants ?? null,
        default_max_bookings_per_slot: activityData.defaultMaxBookingsPerSlot ?? null,
        main_image: activityData.mainImage,
        images: activityData.images || [],
        features: activityData.features || [],
        min_age: activityData.minAge,
        max_age: activityData.maxAge,
        what_to_wear: activityData.whatToWear || [],
        included: activityData.included || [],
        not_included: activityData.notIncluded || [],
        important_rules: activityData.importantRules || [],
        cancellation_conditions: activityData.cancellationConditions || [],
        pmr_access: activityData.pmrAccess || false,
        parking_available: activityData.parkingAvailable || false,
        is_active: isActive,
        popular: false,
        rating: 0,
        review_count: 0,
      })
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .single();

    if (error) {
      console.error('Error creating activity:', error);
      throw error;
    }

    return transformActivity(data);
  } catch (error) {
    console.error('Failed to create activity:', error);
    throw error;
  }
}

/**
 * Update an existing activity
 * @param activityId - Activity UUID to update
 * @param activityData - Partial activity data to update (same shape as create, minus professionalId)
 * @returns Updated activity
 */
export async function updateActivity(
  activityId: string,
  activityData: {
    categoryId: string;
    /** Custom display name when category is "autre". */
    customCategoryName?: string | null;
    name: string;
    shortDescription: string;
    address: string;
    city: string;
    postalCode: string;
    latitude?: number;
    longitude?: number;
    priceTiers: { maxParticipants: number; pricePerPerson: number }[];
    priceRange: string;
    duration: number;
    capacity: number;
    minParticipants?: number | null;
    defaultMaxBookingsPerSlot?: number | null;
    mainImage: string;
    images?: string[];
    features?: string[];
    minAge?: number;
    maxAge?: number;
    whatToWear?: string[];
    included?: string[];
    notIncluded?: string[];
    importantRules?: string[];
    cancellationConditions?: string[];
    pmrAccess?: boolean;
    parkingAvailable?: boolean;
    isActive?: boolean;
  }
): Promise<Activity> {
  try {
    const updatePayload: Record<string, unknown> = {
      category_id: activityData.categoryId,
      custom_category_name: activityData.customCategoryName ?? null,
      name: activityData.name,
      short_description: activityData.shortDescription,
      address: activityData.address,
      city: activityData.city,
      postal_code: activityData.postalCode,
      latitude: activityData.latitude ?? null,
      longitude: activityData.longitude ?? null,
      price_tiers: activityData.priceTiers,
      price_range: activityData.priceRange,
      duration: activityData.duration,
      capacity: activityData.capacity,
      min_participants: activityData.minParticipants ?? null,
      default_max_bookings_per_slot: activityData.defaultMaxBookingsPerSlot ?? null,
      main_image: activityData.mainImage,
      images: activityData.images || [],
      features: activityData.features || [],
      min_age: activityData.minAge ?? null,
      max_age: activityData.maxAge ?? null,
      what_to_wear: activityData.whatToWear || [],
      included: activityData.included || [],
      not_included: activityData.notIncluded || [],
      important_rules: activityData.importantRules || [],
      cancellation_conditions: activityData.cancellationConditions || [],
      pmr_access: activityData.pmrAccess ?? false,
      parking_available: activityData.parkingAvailable ?? false,
    };
    if (activityData.isActive !== undefined) {
      updatePayload.is_active = activityData.isActive;
    }
    const { data, error } = await supabase
      .from('activities')
      .update(updatePayload)
      .eq('id', activityId)
      .select(`
        *,
        category:categories(id, slug, name)
      `)
      .single();

    if (error) {
      console.error('Error updating activity:', error);
      throw error;
    }

    return transformActivity(data);
  } catch (error) {
    console.error('Failed to update activity:', error);
    throw error;
  }
}

/**
 * Get all categories for activity creation
 * @returns Array of categories
 */
export async function getCategories(): Promise<Array<{ id: string; name: string; slug: string }>> {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, slug')
      .order('name');

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }
}
